/* eslint-disable no-unused-vars */
// ^ ESLint config does not include eslint-plugin-react, so JSX-only usage of
//   @react-pdf/renderer primitives (Document, Page, Text, View) and the local
//   composition helpers (Header, Footer, TopSummary, etc.) is incorrectly
//   flagged as unused. Disable for this file only.
/**
 * packetGenerator.jsx — LoanKnow shareable PDF packets
 *
 * Two templates:
 *   1. RecommendationPacket — produced after the assessment. Recommended program(s),
 *      readiness score with category breakdown, borrower's stated profile, next steps.
 *   2. UnderwritingPacket — produced after the full application + Owen verdict. Same
 *      content as v1 plus the URLA-style application data, Owen's underwriting
 *      findings, and the proof-document checklist.
 *
 * Both packets are designed to be sent directly to a loan officer or realtor: brand
 * mark, clean typography, structured findings, no marketing language. The recipient
 * sees a packet that looks like a vetted file, not a sales sheet.
 */
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────
const C = {
  ink: '#111111',
  ink2: '#444444',
  ink3: '#777777',
  rule: '#e5e5e5',
  brand: '#0f172a',
  accent: '#0d6efd',
  good: '#198754',
  warn: '#fd7e14',
  bad: '#dc3545',
  bg: '#fafafa',
  bgSoft: '#f4f6f8',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 64,
    paddingHorizontal: 48,
    fontSize: 10.5,
    color: C.ink,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: C.rule,
  },
  brandWrap: { flexDirection: 'column' },
  brandMark: {
    fontSize: 16,
    fontWeight: 700,
    color: C.brand,
    letterSpacing: -0.4,
  },
  brandTag: { fontSize: 8.5, color: C.ink3, marginTop: 2 },
  packetType: {
    fontSize: 8.5,
    color: C.ink3,
    textAlign: 'right',
  },
  packetDate: {
    fontSize: 8.5,
    color: C.ink3,
    textAlign: 'right',
    marginTop: 2,
  },
  h1: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
    color: C.ink,
  },
  h2: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 18,
    marginBottom: 8,
    color: C.ink,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  h3: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: C.ink,
  },
  intro: {
    fontSize: 10.5,
    color: C.ink2,
    marginBottom: 14,
  },

  // Summary card (top of doc)
  summaryCard: {
    flexDirection: 'row',
    backgroundColor: C.bg,
    borderWidth: 1,
    borderColor: C.rule,
    borderRadius: 6,
    padding: 16,
    marginBottom: 18,
  },
  summaryScoreCol: {
    width: 110,
    paddingRight: 16,
    borderRightWidth: 1,
    borderRightColor: C.rule,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreNum: {
    fontSize: 36,
    fontWeight: 700,
    color: C.brand,
    lineHeight: 1.05,
  },
  scoreOutOf: { fontSize: 9, color: C.ink3 },
  scoreLabel: {
    fontSize: 9,
    color: C.ink2,
    marginTop: 6,
    textAlign: 'center',
  },
  summaryDetailCol: { flex: 1, paddingLeft: 16 },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  summaryLabel: { width: 110, fontSize: 9, color: C.ink3 },
  summaryValue: { flex: 1, fontSize: 10, color: C.ink },

  // Section card
  card: {
    borderWidth: 1,
    borderColor: C.rule,
    borderRadius: 6,
    padding: 14,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: C.ink,
  },
  cardMeta: {
    fontSize: 9,
    color: C.ink3,
    marginBottom: 8,
  },
  bestMatchPill: {
    fontSize: 8,
    fontWeight: 700,
    color: 'white',
    backgroundColor: C.accent,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginBottom: 6,
    alignSelf: 'flex-start',
  },

  // Score categories
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  categoryLabel: { width: 150, fontSize: 9, color: C.ink2 },
  categoryBarTrack: {
    flex: 1,
    height: 6,
    backgroundColor: C.rule,
    borderRadius: 3,
    marginHorizontal: 8,
    overflow: 'hidden',
  },
  categoryBarFill: {
    height: 6,
    backgroundColor: C.accent,
    borderRadius: 3,
  },
  categoryPts: { width: 50, fontSize: 9, color: C.ink2, textAlign: 'right' },

  // Two-column field grid (used in profile & application)
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  gridCell: {
    width: '50%',
    flexDirection: 'column',
    paddingRight: 8,
    marginBottom: 6,
  },
  fieldLabel: { fontSize: 8.5, color: C.ink3, marginBottom: 1 },
  fieldValue: { fontSize: 10, color: C.ink },

  // Bullet list
  bulletRow: { flexDirection: 'row', marginBottom: 3 },
  bulletDot: { width: 10, fontSize: 10, color: C.ink2 },
  bulletText: { flex: 1, fontSize: 10, color: C.ink2 },

  // Verdict findings
  finding: {
    paddingLeft: 8,
    borderLeftWidth: 3,
    marginBottom: 6,
    paddingTop: 2,
    paddingBottom: 2,
  },
  findingPass: { borderLeftColor: C.good },
  findingCondition: { borderLeftColor: C.warn },
  findingFail: { borderLeftColor: C.bad },
  findingTitle: { fontSize: 10, fontWeight: 700, color: C.ink },
  findingDetail: { fontSize: 9.5, color: C.ink2 },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: C.rule,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 7.5, color: C.ink3 },
});

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '—';
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtNum = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
};

const fmtDate = (d = new Date()) =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const titleCase = (s) =>
  String(s || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const value = (v, fallback = '—') => {
  if (v === undefined || v === null || v === '') return fallback;
  return String(v);
};

// ─────────────────────────────────────────────────────────────
// Reusable header / footer
// ─────────────────────────────────────────────────────────────
const Header = ({ packetType, borrowerName }) => (
  <View style={styles.header} fixed>
    <View style={styles.brandWrap}>
      <Text style={styles.brandMark}>LoanKnow</Text>
      <Text style={styles.brandTag}>Mortgage readiness, by Owen AI</Text>
    </View>
    <View>
      <Text style={styles.packetType}>{packetType}</Text>
      <Text style={styles.packetDate}>
        {borrowerName ? `Prepared for ${borrowerName} · ` : ''}{fmtDate()}
      </Text>
    </View>
  </View>
);

const Footer = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>
      LoanKnow · Mortgage Readiness Report · This is informational only and is not a credit
      decision, commitment to lend, or guarantee of approval.
    </Text>
    <Text
      style={styles.footerText}
      render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
    />
  </View>
);

// ─────────────────────────────────────────────────────────────
// Top summary card (used in both packets)
// ─────────────────────────────────────────────────────────────
const TopSummary = ({ score, scoreLabel, recommendedProduct, productSummary, intent }) => (
  <View style={styles.summaryCard}>
    <View style={styles.summaryScoreCol}>
      <Text style={styles.scoreNum}>{score || 0}</Text>
      <Text style={styles.scoreOutOf}>/ 100</Text>
      <Text style={styles.scoreLabel}>{scoreLabel}</Text>
    </View>
    <View style={styles.summaryDetailCol}>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Recommended program</Text>
        <Text style={styles.summaryValue}>
          {recommendedProduct?.displayName || recommendedProduct?.name || '—'}
        </Text>
      </View>
      {productSummary ? (
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Program details</Text>
          <Text style={styles.summaryValue}>{productSummary}</Text>
        </View>
      ) : null}
      {intent ? (
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Loan purpose</Text>
          <Text style={styles.summaryValue}>{intent}</Text>
        </View>
      ) : null}
    </View>
  </View>
);

// ─────────────────────────────────────────────────────────────
// Score breakdown
// ─────────────────────────────────────────────────────────────
const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown || !breakdown.categories) return null;
  return (
    <View>
      <Text style={styles.h2}>Readiness Breakdown</Text>
      {breakdown.categories.map((c) => {
        const pct = c.maxPoints ? Math.round((c.points / c.maxPoints) * 100) : 0;
        return (
          <View key={c.id} style={styles.categoryRow}>
            <Text style={styles.categoryLabel}>{c.label}</Text>
            <View style={styles.categoryBarTrack}>
              <View style={[styles.categoryBarFill, { width: `${pct}%` }]} />
            </View>
            <Text style={styles.categoryPts}>
              {c.points}/{c.maxPoints}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Borrower stated profile
// ─────────────────────────────────────────────────────────────
const StatedProfile = ({ profile }) => {
  if (!profile || profile.length === 0) return null;
  return (
    <View>
      <Text style={styles.h2}>Borrower Stated Profile</Text>
      <View style={styles.grid}>
        {profile.map((row) => (
          <View key={row.label} style={styles.gridCell}>
            <Text style={styles.fieldLabel}>{row.label}</Text>
            <Text style={styles.fieldValue}>{value(row.value)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Recommended programs — ranked list
// ─────────────────────────────────────────────────────────────
const Recommendations = ({ products }) => {
  if (!products || products.length === 0) return null;
  const ranked = products.slice(0, 3);
  return (
    <View>
      <Text style={styles.h2}>Recommended Programs</Text>
      {ranked.map((p, idx) => (
        <View key={p.id || idx} style={styles.card} wrap={false}>
          {idx === 0 ? <Text style={styles.bestMatchPill}>BEST MATCH</Text> : null}
          <Text style={styles.cardTitle}>{p.displayName || p.name}</Text>
          <Text style={styles.cardMeta}>
            {[p.productFamily, p.rateType, p.fixedPeriod ? `${p.fixedPeriod}-yr` : null]
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {p.qualifyingMethod ? (
            <Text style={styles.fieldValue}>{p.qualifyingMethod}</Text>
          ) : null}
          {Array.isArray(p.rationale) && p.rationale.length > 0 ? (
            <View style={{ marginTop: 6 }}>
              <Text style={styles.h3}>Why we matched this</Text>
              {p.rationale.slice(0, 4).map((r, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{r}</Text>
                </View>
              ))}
            </View>
          ) : null}
          <View style={[styles.grid, { marginTop: 6 }]}>
            {p.minCreditScore !== undefined ? (
              <View style={styles.gridCell}>
                <Text style={styles.fieldLabel}>Typical min credit</Text>
                <Text style={styles.fieldValue}>{p.minCreditScore}</Text>
              </View>
            ) : null}
            {p.minDownPayment !== undefined ? (
              <View style={styles.gridCell}>
                <Text style={styles.fieldLabel}>Min down payment</Text>
                <Text style={styles.fieldValue}>{p.minDownPayment}%</Text>
              </View>
            ) : null}
            {p.maxLTV !== undefined ? (
              <View style={styles.gridCell}>
                <Text style={styles.fieldLabel}>Max LTV</Text>
                <Text style={styles.fieldValue}>{p.maxLTV}%</Text>
              </View>
            ) : null}
            {p.investor ? (
              <View style={styles.gridCell}>
                <Text style={styles.fieldLabel}>Investor / channel</Text>
                <Text style={styles.fieldValue}>{p.investor}</Text>
              </View>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Owen verdict (used in v2 packet)
// ─────────────────────────────────────────────────────────────
const VerdictBlock = ({ verdict }) => {
  if (!verdict) return null;
  const findings = verdict.findings || [];
  return (
    <View>
      <Text style={styles.h2}>Owen's Pre-Underwriting Verdict</Text>
      {verdict.summary ? (
        <Text style={[styles.intro, { marginBottom: 10 }]}>{verdict.summary}</Text>
      ) : null}
      {findings.map((f, i) => (
        <View
          key={i}
          style={[
            styles.finding,
            f.status === 'pass' ? styles.findingPass :
            f.status === 'condition' ? styles.findingCondition :
            styles.findingFail,
          ]}
          wrap={false}
        >
          <Text style={styles.findingTitle}>
            {(f.status === 'pass' ? '✓ ' : f.status === 'condition' ? '! ' : '✗ ')}{f.title}
          </Text>
          {f.detail ? <Text style={styles.findingDetail}>{f.detail}</Text> : null}
        </View>
      ))}
      {Array.isArray(verdict.recommendations) && verdict.recommendations.length > 0 ? (
        <View style={{ marginTop: 8 }}>
          <Text style={styles.h3}>Owen's recommendations</Text>
          {verdict.recommendations.map((r, i) => (
            <View key={i} style={styles.bulletRow}>
              <Text style={styles.bulletDot}>•</Text>
              <Text style={styles.bulletText}>{r}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Document checklist
// ─────────────────────────────────────────────────────────────
const DocumentChecklist = ({ documents }) => {
  if (!documents || documents.length === 0) return null;
  return (
    <View>
      <Text style={styles.h2}>Documentation We'll Need to Prove It</Text>
      {documents.map((d, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={styles.bulletDot}>{d.uploaded ? '☑' : '☐'}</Text>
          <Text style={styles.bulletText}>
            {d.name}{d.reason ? ` — ${d.reason}` : ''}
          </Text>
        </View>
      ))}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Application snapshot (URLA-style summary, v2 only)
// ─────────────────────────────────────────────────────────────
const ApplicationSnapshot = ({ application }) => {
  if (!application) return null;
  const sections = Object.entries(application).filter(
    ([, val]) => val && typeof val === 'object' && Object.keys(val).length > 0
  );
  if (sections.length === 0) return null;
  return (
    <View break>
      <Text style={styles.h2}>Application Snapshot</Text>
      {sections.map(([sectionKey, sectionData]) => {
        const entries = Object.entries(sectionData).filter(([, v]) =>
          v !== undefined && v !== null && v !== '' && typeof v !== 'object'
        );
        if (entries.length === 0) return null;
        return (
          <View key={sectionKey} style={styles.card} wrap={false}>
            <Text style={styles.cardTitle}>{titleCase(sectionKey)}</Text>
            <View style={styles.grid}>
              {entries.map(([k, v]) => (
                <View key={k} style={styles.gridCell}>
                  <Text style={styles.fieldLabel}>{titleCase(k)}</Text>
                  <Text style={styles.fieldValue}>{value(v)}</Text>
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────
// Borrower profile builder — collapses the recommendation answers
// into a "what the borrower stated" list.
// ─────────────────────────────────────────────────────────────
function buildStatedProfile(answers = {}, scoreInputs = {}) {
  const purpose = answers.purchaseOrRefinance;
  const isPurchase = purpose === 'purchase';
  const isRefi = purpose === 'refinance';
  const value$ = parseFloat(answers.estimatedValue);
  const dp$ = parseFloat(answers.downPayment);
  const loanAmt = isRefi ? dp$ : Math.max(0, (value$ || 0) - (dp$ || 0));

  const rows = [];
  if (purpose) rows.push({ label: 'Purpose', value: titleCase(purpose) });
  if (answers.refiGoal) rows.push({ label: 'Refi Goal', value: titleCase(answers.refiGoal) });
  if (answers.propertyUse) rows.push({ label: 'Occupancy', value: titleCase(answers.propertyUse) });
  if (answers.propertyType) rows.push({ label: 'Property Type', value: titleCase(answers.propertyType) });
  if (Number.isFinite(value$)) {
    rows.push({ label: isPurchase ? 'Purchase Price' : 'Estimated Value', value: fmtMoney(value$) });
  }
  if (Number.isFinite(dp$)) {
    rows.push({ label: isRefi ? 'Payoff Amount' : 'Down Payment', value: fmtMoney(dp$) });
  }
  if (Number.isFinite(loanAmt) && loanAmt > 0) {
    rows.push({ label: 'Estimated Loan Amount', value: fmtMoney(loanAmt) });
  }
  if (answers.creditScore) rows.push({ label: 'Credit Range', value: answers.creditScore });
  if (answers.loanSizeTier) rows.push({ label: 'Loan Size Tier', value: titleCase(answers.loanSizeTier) });
  if (answers.annualIncome) rows.push({ label: 'Annual Income', value: fmtMoney(answers.annualIncome) });
  if (answers.monthlyDebts) rows.push({ label: 'Monthly Debts', value: fmtMoney(answers.monthlyDebts) });
  if (answers.employmentStatus) rows.push({ label: 'Employment', value: titleCase(answers.employmentStatus) });
  if (answers.firstTimeHomeBuyer === 'yes') rows.push({ label: 'First-Time Buyer', value: 'Yes' });
  if (answers.militaryService === 'yes') rows.push({ label: 'Military Service', value: 'Yes' });
  if (scoreInputs.dti) rows.push({ label: 'DTI Estimate', value: `${scoreInputs.dti}%` });
  return rows;
}

const productSummaryLine = (p) => {
  if (!p) return '';
  const parts = [
    p.productFamily,
    p.rateType,
    p.fixedPeriod ? `${p.fixedPeriod}-yr` : null,
  ].filter(Boolean);
  return parts.join(' · ');
};

// ─────────────────────────────────────────────────────────────
// PACKET 1 — RecommendationPacket (post-assessment)
// ─────────────────────────────────────────────────────────────
export const RecommendationPacket = ({
  borrowerName,
  recommendedProduct,
  recommendedProducts,
  readinessScore,
  scoreTier,
  scoreBreakdown,
  productRecommendation,
}) => {
  const profile = buildStatedProfile(productRecommendation);
  const purpose = productRecommendation?.purchaseOrRefinance;
  const intent = purpose ? titleCase(purpose) : null;

  return (
    <Document
      title={`LoanKnow Recommendation Packet – ${borrowerName || 'Borrower'}`}
      author="LoanKnow"
    >
      <Page size="LETTER" style={styles.page}>
        <Header packetType="Recommendation Packet" borrowerName={borrowerName} />
        <Text style={styles.h1}>Mortgage Readiness Snapshot</Text>
        <Text style={styles.intro}>
          This packet summarizes what {borrowerName || 'the borrower'} stated, the loan
          program Owen recommended, and a category-by-category breakdown of their
          Mortgage Readiness Score. No credit pull or income verification has occurred
          yet — figures reflect stated values only.
        </Text>

        <TopSummary
          score={readinessScore}
          scoreLabel={scoreTier || 'Readiness Score'}
          recommendedProduct={recommendedProduct}
          productSummary={productSummaryLine(recommendedProduct)}
          intent={intent}
        />

        <ScoreBreakdown breakdown={scoreBreakdown} />
        <StatedProfile profile={profile} />
        <Recommendations products={recommendedProducts} />

        <Text style={styles.h2}>Suggested Next Steps</Text>
        <View style={styles.bulletRow}>
          <Text style={styles.bulletDot}>1.</Text>
          <Text style={styles.bulletText}>
            Reach out to discuss the recommended program and confirm the borrower's goals.
          </Text>
        </View>
        <View style={styles.bulletRow}>
          <Text style={styles.bulletDot}>2.</Text>
          <Text style={styles.bulletText}>
            If the borrower is ready, they can complete the full LoanKnow application
            and Owen pre-underwriting at any time — that produces a richer packet (v2).
          </Text>
        </View>
        <View style={styles.bulletRow}>
          <Text style={styles.bulletDot}>3.</Text>
          <Text style={styles.bulletText}>
            Pull credit, verify income, and structure a final loan estimate.
          </Text>
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

// ─────────────────────────────────────────────────────────────
// PACKET 2 — UnderwritingPacket (post Owen verdict)
// ─────────────────────────────────────────────────────────────
export const UnderwritingPacket = ({
  borrowerName,
  recommendedProduct,
  recommendedProducts,
  readinessScore,
  scoreTier,
  scoreBreakdown,
  productRecommendation,
  applicationData,
  verdict,
  documentChecklist,
}) => {
  const profile = buildStatedProfile(productRecommendation);
  const purpose = productRecommendation?.purchaseOrRefinance;

  return (
    <Document
      title={`LoanKnow Pre-Underwriting Packet – ${borrowerName || 'Borrower'}`}
      author="LoanKnow"
    >
      <Page size="LETTER" style={styles.page}>
        <Header packetType="Pre-Underwriting Packet" borrowerName={borrowerName} />
        <Text style={styles.h1}>Pre-Underwritten File</Text>
        <Text style={styles.intro}>
          This packet contains {borrowerName || 'the borrower'}'s full mortgage
          application along with Owen's pre-underwriting findings against the
          recommended program. Use it as a head start — credit pull, document
          verification, and final disclosures are still required.
        </Text>

        <TopSummary
          score={readinessScore}
          scoreLabel={scoreTier || 'Readiness Score'}
          recommendedProduct={recommendedProduct}
          productSummary={productSummaryLine(recommendedProduct)}
          intent={purpose ? titleCase(purpose) : null}
        />

        <VerdictBlock verdict={verdict} />
        <ScoreBreakdown breakdown={scoreBreakdown} />
        <StatedProfile profile={profile} />
        <DocumentChecklist documents={documentChecklist} />
        <Recommendations products={recommendedProducts} />
        <ApplicationSnapshot application={applicationData} />

        <Footer />
      </Page>
    </Document>
  );
};

// ─────────────────────────────────────────────────────────────
// Browser-side PDF builders
// ─────────────────────────────────────────────────────────────

/**
 * Build a Blob for either packet. Used by the browser download button and to
 * upload to Supabase Storage before invoking the send-packet edge function.
 */
export async function buildRecommendationPacketBlob(props) {
  return await pdf(<RecommendationPacket {...props} />).toBlob();
}

export async function buildUnderwritingPacketBlob(props) {
  return await pdf(<UnderwritingPacket {...props} />).toBlob();
}

/**
 * Download a packet immediately (no email).
 */
export async function downloadPacket(packetVersion, props) {
  const blob =
    packetVersion === 'underwriting'
      ? await buildUnderwritingPacketBlob(props)
      : await buildRecommendationPacketBlob(props);

  const filename =
    packetVersion === 'underwriting'
      ? `LoanKnow-PreUnderwriting-${(props.borrowerName || 'Packet').replace(/\s+/g, '_')}.pdf`
      : `LoanKnow-Recommendation-${(props.borrowerName || 'Packet').replace(/\s+/g, '_')}.pdf`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convert a Blob to base64 (no data URL prefix). Used to ship the packet to the
 * Resend-backed edge function without requiring a Storage round-trip.
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const result = String(reader.result || '');
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

export default {
  RecommendationPacket,
  UnderwritingPacket,
  buildRecommendationPacketBlob,
  buildUnderwritingPacketBlob,
  downloadPacket,
  blobToBase64,
};
